<template>
  <!-- The trial signup page, in the marketing site's look since 5 Oct 2026
       (Muhammad): its header and footer, a hero like its PageHero, and the
       form in a card like the Buy IMPROVE page's. -->
  <layout-public :legacy="false">
    <div class="signup">
      <section class="signup-hero">
        <v-container class="signup-hero__inner">
          <p class="signup-hero__eyebrow">Free trial</p>
          <h1 class="signup-hero__title">Start your free trial</h1>
          <p class="signup-hero__lead">
            Get full access for 5 users for 14 days. No credit card required.
          </p>
        </v-container>
      </section>

      <v-container class="signup-body">
        <div class="signup-grid">
          <section class="signup-steps" aria-labelledby="signup-steps-title">
            <h2 id="signup-steps-title" class="signup-steps__title">How it works</h2>
            <ol class="signup-steps__list">
              <li v-for="(step, index) in steps" :key="index" class="signup-step">
                <span class="signup-step__index">{{ index + 1 }}</span>
                <span class="signup-step__text">{{ step }}</span>
              </li>
              <li class="signup-step">
                <span class="signup-step__index">{{ steps.length + 1 }}</span>
                <span class="signup-step__text">
                  If you still have questions
                  <a :href="webinarUrl" target="_blank" rel="noopener">book our 20 minute webinar</a>
                  and experience IMPROVE Live.
                </span>
              </li>
            </ol>
            <router-link to="/forgot-password" class="signup-steps__forgot">
              Forgotten your password? Click here
            </router-link>
          </section>

          <free-trial-form class="signup-form" />
        </div>
      </v-container>
    </div>
  </layout-public>
</template>

<script>
import LayoutPublic from "@/layouts/Signup";
import FreeTrialForm from "@/components/FreeTrialForm.vue";
import { websiteUrl } from "@/config.js";

export default {
  name: "Signup",
  components: {
    LayoutPublic,
    FreeTrialForm
  },
  data() {
    return {
      // Steps 3 and 4 in Muhammad's words (7 Oct 2026).
      steps: [
        "Complete the form",
        "Gain instant access",
        "On purchase of your IMPROVE package you can upload the rest of your users."
      ],
      // The website's IMPROVE Live booking, which needs no account.
      webinarUrl: websiteUrl("/improve-live") + "#book"
    };
  }
};
</script>

<style scoped>
.signup {
  --navy: #002458;
  --teal: #08a0a8;
  --grey: #6b7280;
  --line: #e6ebf1;
  --tint: #eef8f8;
  font-family: "Sora", sans-serif;
  background: #f7f9fb;
  padding-bottom: 72px;
  text-align: left;
}

.signup-hero {
  background: linear-gradient(180deg, var(--tint) 0%, #f7f9fb 78%);
  padding: 56px 0 28px;
  text-align: center;
}

.signup-hero__inner {
  max-width: 820px;
}

.signup-hero__eyebrow {
  display: inline-block;
  background: var(--teal);
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  padding: 6px 14px;
  border-radius: 20px;
  margin: 0 0 18px;
}

.signup-hero__title {
  color: var(--navy);
  font-size: 40px;
  line-height: 1.15;
  font-weight: 700;
  margin: 0 0 14px;
}

.signup-hero__lead {
  color: var(--grey);
  font-size: 18px;
  line-height: 1.55;
  margin: 0 auto;
  max-width: 640px;
}

.signup-body {
  max-width: 1180px;
  padding-top: 24px;
}

/* Steps beside the form on a wide screen; on a narrow one the form comes
   first, so nobody scrolls past the steps to reach it. */
.signup-grid {
  display: grid;
  grid-template-columns: minmax(0, 5fr) minmax(0, 7fr);
  gap: 40px;
  align-items: start;
}

.signup-steps {
  padding-top: 12px;
}

.signup-steps__title {
  color: var(--navy);
  font-size: 22px;
  font-weight: 700;
  margin: 0 0 20px;
}

.signup-steps__list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.signup-step {
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding-bottom: 24px;
}

/* The dotted line from one number to the next. */
.signup-step:not(:last-child)::before {
  content: "";
  position: absolute;
  left: 17px;
  top: 40px;
  bottom: 6px;
  border-left: 2px dotted #b8e4e7;
}

.signup-step__index {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: var(--teal);
  color: #fff;
  font-size: 15px;
  font-weight: 700;
}

.signup-step__text {
  padding-top: 7px;
  color: var(--navy);
  font-size: 16px;
  font-weight: 600;
  line-height: 1.45;
}

.signup-step__text a {
  color: var(--teal);
  font-weight: 700;
  text-decoration: none;
}

.signup-steps__forgot {
  display: inline-block;
  margin-top: 4px;
  color: var(--teal);
  font-size: 14px;
  font-weight: 600;
  text-decoration: none;
}

.signup-steps__forgot:hover,
.signup-step__text a:hover {
  text-decoration: underline;
}

@media (max-width: 959px) {
  .signup-grid {
    grid-template-columns: minmax(0, 1fr);
    gap: 32px;
  }

  .signup-form {
    order: -1;
  }
}

@media (max-width: 600px) {
  .signup-hero {
    padding: 36px 0 20px;
  }

  .signup-hero__title {
    font-size: 30px;
  }

  .signup-hero__lead {
    font-size: 16px;
  }
}
</style>
